'use client';

// import { Product } from '@castlery/modules-product-domain';
import { Divider, Loading, Pagination, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { selectProduct } from '@castlery/modules-product-domain';
import { Rating, SortBy, type SortOption } from '@castlery/shared-components';
import { CountryCode, CountryName } from '@castlery/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProductReviewsItem } from './components/product-reviews-items/product-reviews-item';
import { OrderItems, useGlobalReviews } from './hooks/useGlobalReviews';
import { EVENT_PDP_REVIEW_SECTION } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';
// interface ProductReviewsClientProps {
//   // productData: Product;
// }

export function ProductReviewsClient() {
  // const { productData } = props;
  const productData = useSelector(selectProduct);
  const {
    orderItems,
    setOrderItems,
    currentCountry,
    setCurrentCountry,
    pageNumber,
    setPageNumber,
    reviewsLoading,
    reviews,
    averageRating,
    totalCount,
    totalPages,
  } = useGlobalReviews(productData);
  const { desktop, tablet, mobile } = useBreakpoints();
  const dispatch = useAppDispatch();

  const sortOptions: SortOption[] = [
    { value: 'recommended', label: 'RECOMMENDED', dataLabel: 'Recommended' },
    { value: 'most_recent', label: 'MOST RECENT', dataLabel: 'Most Recent' },
    { value: 'rating_high_to_low', label: 'RATING: HIGH TO LOW', dataLabel: 'Rating - High to Low' },
    { value: 'rating_low_to_high', label: 'RATING: LOW TO HIGH', dataLabel: 'Rating - Low to High' },
    { value: 'with_image', label: 'WITH PICTURES', dataLabel: 'With Pictures' },
    { value: 'product_self', label: 'VIEW PRODUCT ITSELF', dataLabel: 'View Product Itself' },
  ];

  const locationOptions: SortOption[] = Object.keys(CountryCode).map((country) => ({
    value: country,
    label: CountryName[country as CountryCode],
  }));

  const [showLoading, setShowLoading] = useState(true);

  const sortDisplay = useMemo(() => {
    return 'SORT BY';
  }, [desktop]);

  const locationDisplay = useMemo(() => {
    return 'LOCATION';
  }, [desktop]);

  useEffect(() => {
    if (reviews !== undefined) {
      setShowLoading(false);
    }
  }, [reviews]);

  const handleTrack = useCallback(
    async (action: string, label: string) => {
      await dispatch(EVENT_PDP_REVIEW_SECTION({ action, label }));
    },
    [dispatch]
  );

  const handleSortChange = (value: string) => {
    setPageNumber(1);
    setOrderItems(value as OrderItems);
    const target = sortOptions.find((option) => option.value === value);
    if (target) {
      handleTrack('review_dropdown', target.dataLabel);
    }
  };

  const handleLocationChange = (value: string) => {
    setPageNumber(1);
    setCurrentCountry(value as CountryCode);
  };

  return (
    <>
      <Stack
        sx={(theme) => ({
          paddingLeft: desktop ? theme.spacing(7) : theme.spacing(6),
          paddingRight: desktop ? theme.spacing(7) : theme.spacing(6),
          paddingTop: theme.spacing(9),
          paddingBottom: theme.spacing(9),
        })}
        id="reviews-container"
      >
        <Stack
          gap={(theme) => theme.spacing(3)}
          alignItems={'flex-start'}
          mb={(theme) => (desktop ? theme.spacing(3) : theme.spacing(5))}
        >
          <Typography level="h2">What our customers are saying</Typography>
          <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={(theme) => theme.spacing(2)}>
            <Rating
              rating={averageRating}
              size={15}
              innerType="outline"
              innerColor={'#A45B37'}
              outerColor={'transparent'}
            />
            <Typography level="body1">{`Based on ${totalCount} ${
              totalCount > 1 ? 'reviews' : 'review'
            } globally`}</Typography>
          </Stack>
        </Stack>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={(theme) => ({
            my: !desktop ? theme.spacing(2) : undefined,
          })}
        >
          <SortBy
            value={orderItems}
            options={sortOptions}
            onChange={handleSortChange}
            label={sortDisplay}
            labelProps={{
              sx: {
                color: 'var(--fortress-palette-brand-terracotta-500)',
              },
            }}
          />
          <SortBy
            value={currentCountry}
            options={locationOptions}
            onChange={handleLocationChange}
            label={locationDisplay}
            labelProps={{
              sx: {
                color: 'var(--fortress-palette-brand-terracotta-500)',
              },
            }}
          />
        </Stack>
        <Divider
          sx={(theme) => ({
            mt: desktop ? theme.spacing(3) : undefined,
          })}
        />
        {showLoading ? (
          <Stack justifyContent={'center'} alignItems={'center'} mt={desktop ? 7 : mobile ? 8 : 5}>
            <Loading theme="dark" />
          </Stack>
        ) : (
          <>
            <Stack justifyContent={'center'} alignItems={'center'}>
              {reviews?.map((review) => {
                return <ProductReviewsItem key={review.id} review={review} />;
              })}
              {reviews?.length === 0 && !reviewsLoading && (
                <Typography level="body1" mt={desktop ? 7 : mobile ? 8 : 5}>
                  There are currently no reviews available.
                </Typography>
              )}
            </Stack>
            <Pagination
              sx={(theme: any) => ({
                marginTop: desktop ? theme.spacing(7) : mobile ? theme.spacing(8) : theme.spacing(5),
              })}
              count={totalPages}
              page={pageNumber}
              siblingCount={mobile ? 0 : 1}
              onChange={(event: React.ChangeEvent<unknown> | null, page: number) => {
                setPageNumber(page);
                handleTrack('select_review_page', `${page}`);
              }}
            />
          </>
        )}
      </Stack>
    </>
  );
}
